import React from 'react';
import {
  Wrench,
  Thermometer,
  Zap,
  Droplets,
  Flame,
  Cable,
  ClipboardCheck,
  Ruler,
  Layers3,
  Building,
  Hotel,
  Home,
  Snowflake,
  Landmark,
  Factory,
  ShoppingBag,
  MapPin,
  Phone,
  Mail,
  Globe,
  ShieldCheck,
  FileCheck2,
  Users,
  Gauge,
  Workflow,
  Handshake,
} from 'lucide-react';

import {
  usePageSeo,
  Section,
  SectionHeading,
  CompanyHero,
  FeatureCard,
  StatCard,
  ListPanel,
  AssuranceStrip,
  CTABanner,
  GroupMemberSection,
} from '../../components/company/CompanyBlocks';
import { SeoHead } from '../../components/ui/SeoHead';
import { Layout } from '../../components/layout/Layout';

/* ================================================================== *
 *  KIMKO (UAE) — /companies/kimko
 *  Content sourced from KIMKO Electromechanical Works L.L.C.
 *  Company Profile 2025. Design system: unchanged.
 * ================================================================== */

const coreServices = [
  {
    icon: <Thermometer className="w-7 h-7" />,
    label: 'Mechanical',
    title: 'HVAC Design & Engineering',
    body:
      'Chiller plants, central cooling, ducted and VRF systems, kitchen extract hoods, fire and smoke extraction. Load calculation, equipment selection, duct and pipe sizing, and psychrometric validation before procurement.',
    tags: ['Chiller Plants', 'Central Cooling', 'Smoke Extract'],
  },
  {
    icon: <Zap className="w-7 h-7" />,
    label: 'Electrical',
    title: 'Power Systems & Distribution',
    body:
      'LV and MV distribution design, switchboards, standby generators, UPS systems, transformers, load schedules, cable sizing, and earthing design for residential, commercial and industrial buildings.',
    tags: ['LV / MV', 'Switchgear', 'Standby Power'],
  },
  {
    icon: <Droplets className="w-7 h-7" />,
    label: 'Public Health',
    title: 'Plumbing & Drainage Design',
    body:
      'Domestic water, hot water, above and below ground drainage, pumping stations, sewage and waste water treatment plants, irrigation and swimming pool services — designed to authority drainage standards.',
    tags: ['Pump Stations', 'STP / WWTP', 'Drainage'],
  },
  {
    icon: <Flame className="w-7 h-7" />,
    label: 'Life Safety',
    title: 'Fire Protection Engineering',
    body:
      'Sprinkler, standpipe, fire pump and suppression system design with hydraulic calculations. Prepared for civil defence submission and coordinated with the architectural compartmentation strategy.',
    tags: ['Sprinkler', 'Fire Pumps', 'Hydraulics'],
  },
  {
    icon: <Cable className="w-7 h-7" />,
    label: 'ELV / Low Current',
    title: 'Low Current & Smart Systems',
    body:
      'Fire alarm, emergency lighting, CCTV, access control, structured cabling, telephony and BMS integration — specified as one coordinated low-current package rather than isolated system tenders.',
    tags: ['Fire Alarm', 'CCTV', 'BMS'],
  },
  {
    icon: <ClipboardCheck className="w-7 h-7" />,
    label: 'Regulatory',
    title: 'Authority Approvals & Submissions',
    body:
      'Drawing preparation, submission and follow-through with Dubai Municipality, DDA, master developers and utility providers — including comment closure, resubmission and approval tracking to release.',
    tags: ['DM', 'DDA', 'Master Developer'],
  },
];

const industries = [
  { icon: <Home className="w-6 h-6" />, title: 'Private & Signature Villas', body: 'Palm Jumeirah, Dubai Hills, Nadd Al Shiba, Umm Suqeim, Al Barari and Meadows villa programmes — including full modification and fit-out scopes.' },
  { icon: <Building className="w-6 h-6" />, title: 'Residential Towers', body: 'High-rise residential and mixed residential/commercial buildings from G+3 to G+90, including full-package MEP for tower developments.' },
  { icon: <Hotel className="w-6 h-6" />, title: 'Hospitality', body: 'Hotel and hotel-apartment developments, including maintenance and completion of full MEP works on live hospitality assets.' },
  { icon: <ShoppingBag className="w-6 h-6" />, title: 'Retail & Mixed Use', body: 'Shopping centres, mixed-use hotel and retail schemes, restaurants and food-and-beverage fit-outs with kitchen extract and grease systems.' },
  { icon: <Landmark className="w-6 h-6" />, title: 'Government & Institutional', body: 'Government office and customs facilities, primary schools and mosque projects delivered through design and authority approval stages.' },
  { icon: <Snowflake className="w-6 h-6" />, title: 'Cold Storage & Logistics', body: 'Cold storage and storage facilities requiring refrigeration load design, insulated envelopes and process-driven mechanical services.' },
  { icon: <Factory className="w-6 h-6" />, title: 'Industrial Facilities', body: 'Industrial buildings requiring power distribution, switchboards, emergency power stations and UPS systems to production requirements.' },
  { icon: <Layers3 className="w-6 h-6" />, title: 'Events & Special Projects', body: 'Pavilion and event-scale electrical works delivered against fixed, non-movable completion dates.' },
];

const capabilities = [
  {
    title: 'Design Management',
    items: [
      'Concept, schematic and detailed MEP design development',
      'Design review of consultant and third-party packages',
      'Value engineering with cost and operating-life impact stated',
      'Specification writing and equipment schedules',
      'Design change control with documented sign-off',
    ],
    icon: <Ruler className="w-5 h-5" />,
  },
  {
    title: 'MEP Coordination',
    items: [
      'Multi-service coordinated shop drawings and layouts',
      'Clash detection and services zoning above ceiling',
      'Builder\'s work, penetration and sleeve drawings',
      'Interface management between MEP, structure and finishes',
      'As-built drawing production and handover documentation',
    ],
    icon: <Workflow className="w-5 h-5" />,
  },
  {
    title: 'Project Engineering Management',
    items: [
      'Technical submittals, material approvals and comment closure',
      'Procurement support and vendor technical evaluation',
      'Site engineering supervision and quality inspection',
      'Testing, commissioning and system demonstration',
      'Snagging, de-snagging and defects liability support',
    ],
    icon: <Gauge className="w-5 h-5" />,
  },
];

const projects = [
  { title: 'Palm Jumeirah Villas', meta: 'Multiple plots — Palm Jumeirah, Dubai', body: 'Full MEP packages across a continuing programme of signature and private villas on Palm Jumeirah, delivered for developer and contractor clients under master-developer approval requirements.' },
  { title: 'Dubai Hills Villas', meta: 'Dubai Hills Estate, Dubai', body: 'MEP design and delivery on private villa plots within Dubai Hills Estate, including HVAC works and full residential services packages.' },
  { title: 'Dubai Customs — Port Rashid', meta: 'Plot 321-175, Port Rashid Road, Dubai', body: 'Government facility works including car park modification and interior MEP services for operational customs premises.' },
  { title: 'Hotel & Hotel-Apartment Developments', meta: 'Wadi Al Safa and Downtown Dubai', body: 'Maintenance and completion of full MEP works on hotel apartment buildings, plus design and authority approvals for large mixed hotel and hotel-apartment towers.' },
  { title: 'Mixed-Use Hotel & Shopping Centre', meta: 'Mamzar Park, Dubai', body: 'MEP scope on a large mixed-use hotel and retail development combining hospitality, retail and back-of-house service requirements.' },
  { title: 'Commercial & Residential Towers', meta: 'G+4P+Roof+15 and G+90 developments, Dubai', body: 'Electrical and full MEP works on high-rise residential and commercial towers, including live-site works on occupied and partially occupied buildings.' },
  { title: 'Cold Storage Facility', meta: 'Nad Al Hamar, Dubai', body: 'B+G+1 cold storage facility — mechanical, electrical and refrigeration-supporting services for a temperature-controlled storage building.' },
  { title: 'Mosque Project', meta: 'Al Warqa\'a First, Dubai', body: 'G+1 mosque delivered through design and authorities approvals for a charitable and religious affairs client.' },
];

const projectGallery = [
  {
    src: '/assets/showcase/kimko/sports-one-tower.jpg',
    title: 'Sports One Tower',
    category: 'High-Rise Commercial',
    location: 'Dubai, UAE',
    desc: 'Full MEP design and installation for a landmark commercial tower — HVAC, power distribution, fire protection and low current systems across all floors.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/fire-pump-room.jpg',
    title: 'Fire Pump Room Installation',
    category: 'Life Safety Systems',
    location: 'Dubai, UAE',
    desc: 'Clarke fire pump sets, sprinkler distribution piping, pressure switches and jockey pumps — designed and installed to civil defence specifications.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/electrical-panel-installation.jpg',
    title: 'Distribution Board Installation',
    category: 'Electrical Systems',
    location: 'Dubai, UAE',
    desc: 'LV distribution boards, MCCB panels, cable routing and termination — installed and tested by KIMKO electrical crews on-site.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/restaurant-fitout.jpg',
    title: 'SLAW Restaurant Fit-Out',
    category: 'Retail & F&B',
    location: 'Dubai, UAE',
    desc: 'Complete MEP package for a restaurant fit-out including kitchen extract, grease extraction, HVAC, lighting, power and fire alarm systems.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/mixed-use-retail-tower.jpg',
    title: 'Mixed-Use Hotel & Shopping Centre',
    category: 'Mixed-Use Development',
    location: 'Mamzar Park, Dubai',
    desc: 'MEP scope on a large mixed-use development combining hospitality, retail and back-of-house service requirements — Zara, Cartier and Fendi retail podium.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/residential-tower.jpg',
    title: 'Residential Tower',
    category: 'High-Rise Residential',
    location: 'Dubai, UAE',
    desc: 'Full MEP package for a G+ residential tower — central cooling, power distribution, pumping stations, drainage and fire protection.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/bathroom-fitout.jpg',
    title: 'Plumbing & Public Health',
    category: 'Public Health Engineering',
    location: 'Dubai, UAE',
    desc: 'Completed bathroom and washroom fit-out — domestic water, hot water, drainage, waterproofing and fixture installation to authority standards.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/luxury-villa-rendering.jpg',
    title: 'Private Villa Design',
    category: 'Signature Villas',
    location: 'Palm Jumeirah, Dubai',
    desc: 'Full MEP packages for signature and private villas — HVAC, electrical, plumbing, fire protection and smart home integration under master-developer approval.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/tower-under-construction.jpg',
    title: 'Tower — MEP Installation Phase',
    category: 'High-Rise Construction',
    location: 'Dubai, UAE',
    desc: 'Live-site MEP installation on a curved residential tower — coordinated multi-discipline works during structural and fit-out phases.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/nakheel-villa-construction.jpg',
    title: 'Nakheel Villa — Palm Jumeirah',
    category: 'Signature Villas',
    location: 'Palm Jumeirah, Dubai',
    desc: 'Villa construction under Nakheel master-developer permits — MEP subcontractor with Rimal Engineering and Intel Space.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/downtown-tower.jpg',
    title: 'Downtown Dubai High-Rise',
    category: 'High-Rise Residential',
    location: 'Downtown Dubai',
    desc: 'Full MEP works on a premium high-rise tower adjacent to Burj Khalifa — electrical, mechanical, plumbing and fire protection for luxury residential units.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/storage-facility.jpg',
    title: 'B+G+1 Storage Facility',
    category: 'Cold Storage & Industrial',
    location: 'Nad Al Hamar, Dubai',
    desc: 'Mechanical, electrical and refrigeration-supporting services for a temperature-controlled storage building under DDA permit — WestZone Building Contracting.',
    span: '',
  },
  {
    src: '/assets/showcase/kimko/villa-construction-site.jpg',
    title: 'Signature Villa — Construction',
    category: 'Villa MEP',
    location: 'Dubai, UAE',
    desc: 'MEP coordination on a private villa under construction — Dubai Municipality permit, B360 Building Contracting, Signature Architecture as consultant.',
    span: '',
  },
];

const advantages = [
  { icon: <Layers3 className="w-7 h-7" />, title: 'Single Integrated MEP Interface', body: 'Mechanical, electrical, plumbing, fire and low current engineered by one team. The client resolves interfaces once, at design stage, instead of arbitrating between four separate specialists on site.' },
  { icon: <ClipboardCheck className="w-7 h-7" />, title: 'Approvals Handled End-to-End', body: 'Submissions are prepared, lodged and driven to approval — including comment closure and resubmission. Approval status is reported as a schedule item, not as an unknown.' },
  { icon: <Users className="w-7 h-7" />, title: 'Engineering Depth In-House', body: 'Senior MEP, mechanical and electrical engineers, dedicated design and drafting, and a directly employed multinational site workforce. Capability is retained, not assembled per project.' },
  { icon: <Handshake className="w-7 h-7" />, title: 'Repeat Client Base', body: 'Clients including KONE, Al-Futtaim Engineering, Shapoorji Pallonji Mideast, ThyssenKrupp, Zener Fire & Security and El Seif award work repeatedly — the clearest available measure of delivery.' },
  { icon: <Building className="w-7 h-7" />, title: 'Developer-Standard Compliance', body: 'Work delivered under Nakheel, Dubai Municipality and DDA permitted conditions on Palm Jumeirah, Dubai Hills and central Dubai plots — where the approval bar is set by the master developer.' },
  { icon: <Wrench className="w-7 h-7" />, title: 'Design Through to Commissioning', body: 'The same organisation that produced the design carries it into shop drawings, installation supervision and commissioning. No handover gap between designer and executor.' },
];

const certifications = [
  { label: 'Legal Entity', value: 'KIMKO Electromechanical Works L.L.C.', note: 'UAE-registered limited liability company, established 2015. Dubai commercial licence and MEP contracting registration current.' },
  { label: 'Municipal', value: 'Dubai Municipality Permitted Works', note: 'Projects executed under Dubai Municipality building permits, with drawings submitted and approved through the DM process.' },
  { label: 'Authority', value: 'DDA Permitted Works', note: 'Works delivered under Dubai Development Authority permits on developments within DDA jurisdiction.' },
  { label: 'Developer', value: 'Master-Developer Approved Scopes', note: 'Executed as MEP subcontractor on Nakheel-controlled Palm Jumeirah plots under developer-issued building permits and approval conditions.' },
  { label: 'Group IMS', value: 'ISO 9001 · 14001 · 45001 Framework', note: 'KIMKO operates within the EnergiPro Group integrated management system framework for quality, environment and occupational health & safety.' },
  { label: 'Records', value: 'Documentation Available on Request', note: 'Trade licence, insurance certificates, project completion references and consultant testimonials issued during prequalification.' },
];

export default function Kimko() {
  return (
    <Layout>
    <main className="bg-white">
      <SeoHead
        title="KIMKO UAE | MEP Engineering & HVAC Design Consultancy Dubai"
        description="KIMKO is the UAE engineering arm of EnergiPro Group — MEP engineering, HVAC design, fire protection, low current, authority approvals and construction support across Dubai."
        canonicalPath="/companies/kimko"
      />
      {/* ---------- 1. HERO ---------- */}
      <CompanyHero
        eyebrow={['MEP ENGINEERING', 'HVAC DESIGN', 'AUTHORITY APPROVALS']}
        icon={<Wrench className="w-6 h-6" />}
        heroLogo="/images/logos/kimko-logo-light-bg.png"
        title="Engineering Confidence Into Every Project"
        tagline="Design. Coordinate. Deliver."
        lead="KIMKO is the United Arab Emirates engineering arm of EnergiPro Group — an MEP engineering and building services consultancy delivering HVAC design, electrical and public health engineering, fire protection, low current systems and authority approvals across Dubai and the wider UAE."
        sub="Established in Dubai in 2015. Villa, tower, hospitality, retail, institutional and cold storage projects delivered from concept design through submission, coordination, installation supervision and commissioning."
        primary={{ label: 'Request Engineering Proposal', to: '/contact' }}
        secondary={{ label: 'Talk to Engineering', to: '/contact' }}
        badges={[
          'Established 2015 — Dubai, UAE',
          'Full MEP Discipline Coverage',
          'DM · DDA · Master-Developer Submissions',
          'Design Through to Commissioning',
        ]}
      />

      {/* ---------- 2. COMPANY OVERVIEW ---------- */}
      <Section tone="white">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Company Overview"
              title="Engineering Excellence in the UAE Built Environment"
              description="KIMKO Electromechanical Works L.L.C. is one of the established MEP engineering organisations in the United Arab Emirates, formed in Dubai in 2015 to give developers, consultants and main contractors a single technical partner for the entire building services package."
            />
            <div className="space-y-5 text-slate-600 leading-relaxed">
              <p>
                Linking the electrical requirements of a large project to its mechanical applications
                is where most building services programmes lose time. It only becomes straightforward
                when one expert team holds both. KIMKO was built around that principle: mechanical,
                electrical, plumbing, fire protection and low current engineered together, by one
                organisation, against one coordinated model.
              </p>
              <p>
                The practice covers HVAC and central cooling, power stations and distribution,
                pumping stations and waste water treatment, plumbing systems, fire fighting systems,
                and low current networks including fire alarm, emergency lighting, CCTV, access
                control and structured cabling. Each is designed as part of a single services
                strategy rather than as an isolated tender package.
              </p>
              <p>
                Delivery is supported by a directly employed multinational workforce and one of the
                larger equipment fleets in Dubai, maintained by dedicated teams. Safety, quality and
                environmental performance are treated as standing obligations, governed by documented
                procedures and reviewed for continual improvement.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 grid sm:grid-cols-2 gap-5">
            <StatCard icon={<Landmark className="w-6 h-6" />} label="Established" value="2015" note="Dubai, United Arab Emirates" />
            <StatCard icon={<Users className="w-6 h-6" />} label="Workforce" value="500+" note="Multinational engineering, supervisory and site personnel" />
            <StatCard icon={<Layers3 className="w-6 h-6" />} label="Disciplines" value="5" note="Mechanical · Electrical · Plumbing · Fire · Low Current" />
            <StatCard icon={<FileCheck2 className="w-6 h-6" />} label="Project Record" value="33+" note="Completed and ongoing UAE projects on the current register" />
          </div>
        </div>
      </Section>

      {/* ---------- 3. CORE SERVICES ---------- */}
      <Section tone="muted">
        <SectionHeading
          eyebrow="Core Services"
          title="Technical Consultancy Across Every Building Service"
          description="Six engineering service lines, coordinated as one package. Each is delivered to UAE authority standards and issued in a form the consultant, contractor and approving authority can act on without rework."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coreServices.map((s) => (
            <FeatureCard key={s.title} {...s} />
          ))}
        </div>
      </Section>

      {/* ---------- 4. INDUSTRIES SERVED ---------- */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Industries Served"
          title="Sectors We Engineer For"
          description="From single signature villas to G+90 towers, government facilities and temperature-controlled storage — each sector carries its own approval route, load profile and commissioning standard."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((i) => (
            <div
              key={i.title}
              className="bg-white border border-slate-200 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-orange-600/50"
            >
              <div className="text-orange-600 mb-4">{i.icon}</div>
              <h3 className="font-heading text-base font-bold uppercase tracking-wide text-slate-900 mb-2">
                {i.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">{i.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- 5. KEY CAPABILITIES ---------- */}
      <Section tone="gradient">
        <SectionHeading
          eyebrow="Key Capabilities"
          title="Design Management · MEP Coordination · Project Delivery"
          description="Three capability blocks that carry a project from an unresolved brief to a commissioned, documented and handed-over building services installation."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {capabilities.map((c) => (
            <ListPanel key={c.title} title={c.title} items={c.items} icon={c.icon} />
          ))}
        </div>
      </Section>

      <AssuranceStrip text="Every service coordinated before it reaches site. Every submission driven to approval." />

      {/* ---------- 6. PROJECT SHOWCASE ---------- */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Project Showcase"
          title="Real Projects. Real Sites. Real Deliveries."
          description="Photographs from KIMKO's completed and ongoing project register — construction sites, installed systems, commissioned buildings and fit-out works across Dubai and the wider UAE."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projectGallery.map((p) => (
            <div
              key={p.title}
              className="group relative overflow-hidden bg-slate-900 min-h-[280px] md:min-h-[320px] cursor-default aspect-[4/3]"
            >
              <img
                src={p.src}
                alt={`${p.title} — ${p.category} project by KIMKO MEP, ${p.location}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
              {/* Category tag */}
              <div className="absolute top-4 left-4">
                <span className="inline-block bg-orange-600 text-white text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5">
                  {p.category}
                </span>
              </div>
              {/* Text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-orange-400 mb-1.5">
                  {p.location}
                </p>
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase tracking-wide text-white mb-2 leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed max-h-0 overflow-hidden opacity-0 group-hover:max-h-40 group-hover:opacity-100 transition-all duration-500">
                  {p.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-slate-400 max-w-3xl leading-relaxed">
          Projects shown were delivered directly or as MEP subcontractor under the named consultants
          and main contractors. Listing indicates work performed, not endorsement.
        </p>
      </Section>

      {/* ---------- 7. GEOGRAPHIC COVERAGE ---------- */}
      <Section tone="dark">
        <SectionHeading
          dark
          eyebrow="Geographic Coverage"
          title="Based in Dubai. Operating Across the UAE."
          description="KIMKO operates from Dubai, with project delivery across the Emirates and technical support available to EnergiPro Group scopes in Saudi Arabia and Egypt."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: 'Dubai', d: 'Head office and primary operating base. Palm Jumeirah, Dubai Hills, Downtown, Port Rashid, Nad Al Hamar, Umm Suqeim, Al Warqa\'a, Ras Al Khor, Al Barari and Meadows.' },
            { t: 'Wider UAE', d: 'Project delivery across the Emirates for developer, consultant and main-contractor clients, mobilised from the Dubai base.' },
            { t: 'Group Reach', d: 'Engineering support to EnergiPro Group scopes in Saudi Arabia and Egypt, giving clients one technical standard across three markets.' },
          ].map((g) => (
            <div key={g.t} className="border border-white/15 bg-white/5 p-7">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-5 h-5 text-orange-500" />
                <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-white">
                  {g.t}
                </h3>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{g.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- 8. COMPETITIVE ADVANTAGES ---------- */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Competitive Advantages"
          title="Why Clients Appoint KIMKO"
          description="Not a drawing office and not a labour supplier — an engineering organisation accountable for the design, the approval and the installed result."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {advantages.map((a) => (
            <FeatureCard key={a.title} icon={a.icon} title={a.title} body={a.body} />
          ))}
        </div>
      </Section>

      {/* ---------- 9. CERTIFICATIONS ---------- */}
      <Section tone="muted">
        <div className="flex justify-center mb-8">
          <img
            src="/images/logos/kimko-logo-light-bg.png"
            alt="KIMKO logo"
            className="w-40 md:w-44 h-auto object-contain"
          />
        </div>
        <SectionHeading
          eyebrow="Certifications & Compliance"
          title="Licensing, Approvals & Management Systems"
          description="Regulatory standing and governance framework under which KIMKO contracts and delivers in the United Arab Emirates."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((c) => (
            <div key={c.value} className="bg-white border border-slate-200 p-7">
              <div className="flex items-center gap-2.5 mb-4">
                <ShieldCheck className="w-5 h-5 text-orange-600" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  {c.label}
                </span>
              </div>
              <h3 className="font-heading text-base font-bold uppercase tracking-wide text-slate-900 mb-3">
                {c.value}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">{c.note}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- 10. CONTACT INFORMATION ---------- */}
      <Section tone="white">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Contact"
              title="Speak to KIMKO Engineering"
              description="Send the drawings, the plot details or the approval problem. A senior engineer reviews the scope before any commercial response is issued."
            />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {[
              { icon: <MapPin className="w-5 h-5" />, label: 'Office', value: 'KIMKO Electromechanical Works L.L.C.', note: 'P.O. Box 81059, Dubai, United Arab Emirates' },
              { icon: <Phone className="w-5 h-5" />, label: 'Telephone', value: '+971 555 412 039', note: '' },
              { icon: <Mail className="w-5 h-5" />, label: 'Email', value: 'gm@kimkocorp.com', note: 'General Manager' },
              { icon: <Globe className="w-5 h-5" />, label: 'Web', value: 'www.kimkocorp.com', note: 'A member of EnergiPro Group' },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-slate-200 p-7">
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="text-orange-600">{c.icon}</span>
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    {c.label}
                  </span>
                </div>
                <p className="font-heading text-base font-bold uppercase tracking-wide text-slate-900 mb-2 break-words">
                  {c.value}
                </p>
                <p className="text-sm text-slate-500 leading-relaxed">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ---------- 11. CTA BANNER ---------- */}
      <CTABanner
        heading="Bring the Engineering Forward"
        sub="Most building services cost is committed before the first duct is hung. Get the MEP design, coordination and approval route resolved at the front of the programme — not on site."
        primary={{ label: 'Request Engineering Proposal', to: '/contact' }}
        secondary={{ label: 'Book a Design Review', to: '/contact' }}
      />

      {/* ---------- 12. MEMBER OF ENERGIPRO GROUP ---------- */}
      <GroupMemberSection current="kimko" />
    </main>
    </Layout>
  );
}
